import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { profile } from "../data/portfolio";
import { handleAnchorClick } from "../lib/scrollToAnchor";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const photoScale = useTransform(scrollYProgress, [0, 1], [1, 1.04]);
  const photoY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.95]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.6, 1], [1, 0.4, 0]);

  const ease = [0.16, 1, 0.3, 1];

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      data-snap-page
      className="relative w-full overflow-hidden bg-black h-[100svh]"
    >
      {/* Background overlays — keep right side lighter on mobile so the photo shows */}
      <div className="absolute inset-0 pointer-events-none z-[1]">
        <div className="absolute inset-0 max-md:bg-[linear-gradient(100deg,rgba(0,0,0,0.92)_0%,rgba(0,0,0,0.75)_32%,rgba(0,0,0,0.25)_52%,transparent_68%)] md:bg-gradient-to-r md:from-black md:via-black/60 md:to-black/15" />
        <div className="absolute inset-0 max-md:hidden md:bg-[radial-gradient(ellipse_at_75%_50%,rgba(0,0,0,0)_0%,rgba(0,0,0,0.35)_55%,rgba(0,0,0,0.75)_100%)]" />
        <motion.div
          style={{ opacity: overlayOpacity }}
          className="absolute inset-x-0 bottom-0 h-1/2 max-md:h-2/5 bg-gradient-to-t from-black/90 via-black/50 to-transparent md:h-2/3 md:from-black md:via-black/70"
        />
        <div className="absolute inset-x-0 top-0 h-24 max-md:h-20 bg-gradient-to-b from-black/80 to-transparent md:h-40" />
      </div>

      <motion.div
        style={{ y: textY, opacity: textOpacity }}
        className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-center px-6 pb-10 pt-24 sm:pt-28 md:h-auto md:min-h-[100svh] md:justify-end md:grid md:grid-cols-12 md:items-end md:gap-6 md:px-10 md:pb-20 md:pt-0 lg:pb-24"
      >
        <div className="relative z-10 w-full md:col-span-6 lg:col-span-7">
          {/* Scrim behind text for readability over the photo */}
          <div
            aria-hidden
            className="pointer-events-none absolute -left-6 -right-4 -top-2 bottom-0 max-w-[62%] rounded-2xl bg-gradient-to-r from-black/95 via-black/85 to-transparent backdrop-blur-[2px] sm:max-w-[58%] md:-right-8 md:max-w-none md:via-black/80 md:to-transparent"
          />

          <div className="relative max-w-[72%] sm:max-w-[68%] md:max-w-none">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease, delay: 0.35 }}
              className="font-display text-3xl sm:text-4xl md:text-6xl lg:text-[4.25rem] leading-[1.05] tracking-[-0.04em] text-white max-w-[14ch] max-md:mt-1 md:max-w-[16ch] [text-shadow:0_2px_16px_rgba(0,0,0,0.8)]"
            >
              {profile.name}
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease, delay: 0.45 }}
              className="mt-3"
            >
              <a
                href={profile.certBadgeHref}
                data-testid="hero-cert-badge"
                className="inline-flex h-7 items-center gap-1.5 rounded-full border border-white/25 bg-black/50 px-2.5 backdrop-blur-md shadow-lg hover:border-white/40 hover:bg-black/65 transition-all duration-300 md:h-8 md:gap-2 md:px-4"
              >
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
                <span className="font-body text-[10px] leading-tight font-medium tracking-wide text-white/95 sm:text-[11px] md:text-[13px]">
                  {profile.certBadge}
                </span>
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease, delay: 0.55 }}
              className="relative mt-3 max-w-md md:mt-4 md:max-w-lg"
            >
              <div
                aria-hidden
                className="pointer-events-none absolute -inset-x-3 -inset-y-2 rounded-xl bg-gradient-to-r from-black via-black/90 to-[#6e6e73]/70 md:to-[#86868b]/50"
              />
              <p className="relative z-10 font-body text-xs sm:text-sm md:text-base text-[#e8e8ed] leading-relaxed [text-shadow:0_1px_10px_rgba(0,0,0,0.7)]">
                {profile.subtagline}
              </p>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease, delay: 0.65 }}
              className="mt-5 font-display text-xl sm:text-2xl md:mt-6 md:text-4xl lg:text-5xl leading-[1.12] tracking-[-0.03em] text-white/95 max-w-[20ch] md:max-w-[22ch] [text-shadow:0_2px_16px_rgba(0,0,0,0.75)]"
            >
              {profile.tagline.before}{" "}
              <span className="text-metal">{profile.tagline.accent}</span>
              <br />
              {profile.tagline.after}
            </motion.h2>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease, delay: 0.95 }}
              className="mt-6 flex flex-wrap items-center gap-3 md:mt-10 md:gap-4"
            >
              <a
                href="#work"
                onClick={(e) => handleAnchorClick(e, "#work")}
                data-testid="hero-cta-work"
                className="inline-flex items-center justify-center gap-2 px-6 h-12 rounded-full bg-white text-black font-body text-sm font-medium hover:bg-[#d2d2d7] transition-all duration-300 active:scale-95"
              >
                See selected work
                <span aria-hidden>↓</span>
              </a>
              <a
                href={`mailto:${profile.email}`}
                data-testid="hero-cta-email"
                className="inline-flex items-center justify-center gap-2 px-6 h-12 rounded-full border border-white/15 text-[#f5f5f7] font-body text-sm font-medium hover:bg-white/5 hover:border-white/30 transition-all duration-300"
              >
                Get in touch
              </a>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, ease, delay: 0.4 }}
          className="max-md:absolute max-md:inset-y-0 max-md:right-0 max-md:z-[8] flex max-md:w-[58%] max-md:items-center max-md:justify-center pointer-events-none md:relative md:top-auto md:inset-auto md:w-auto md:col-span-6 md:h-auto md:items-end md:justify-end md:pr-0 lg:col-span-5"
        >
          <motion.img
            style={{ scale: photoScale, y: photoY }}
            src={profile.photo}
            alt={profile.name}
            className="max-md:h-[min(50vh,400px)] max-md:w-full max-md:max-w-none max-md:object-cover max-md:object-[48%_14%] max-md:brightness-[1.12] max-md:contrast-[1.12] md:h-[64vh] md:w-auto md:max-h-[680px] md:max-w-[min(100%,480px)] md:object-contain md:object-bottom md:brightness-105 md:contrast-110 lg:h-[72vh] lg:max-w-[520px]"
            draggable={false}
          />
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        style={{ opacity: textOpacity }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden flex-col items-center gap-2 md:flex"
      >
        <span className="font-body text-[10px] tracking-[0.4em] uppercase text-[#86868b]">
          Scroll
        </span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="block w-px h-8 bg-gradient-to-b from-white/60 to-transparent"
        />
      </motion.div>
    </section>
  );
}
